import { useState, useEffect, useRef, useCallback } from 'react';
import { isPersistenceEnabled } from '../store/index';
import parentFrameMessaging from '../services/ParentFrameMessaging';

// Key for storing timer end time in localStorage
const TIMER_STORAGE_KEY = 'playmerce_timer_end_time';

export const useGameTimer = (durationSeconds: number, onTimerComplete?: () => void) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    // Don't initialize from localStorage if we don't have a valid duration
    if (!durationSeconds || durationSeconds <= 0) {
      return 0;
    }

    // Only load from localStorage if persistence is enabled
    if (isPersistenceEnabled()) {
      const savedEndTime = localStorage.getItem(TIMER_STORAGE_KEY);
      if (savedEndTime) {
        const endTime = parseInt(savedEndTime, 10);
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        return remaining;
      }
    }
    return durationSeconds;
  });

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = useCallback(() => {
    // Don't start timer if we don't have a valid duration
    if (!durationSeconds || durationSeconds <= 0) {
      return;
    }

    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    timerIntervalRef.current = setInterval(() => {
      if (!isPersistenceEnabled()) {
        return;
      }

      const savedEndTime = localStorage.getItem(TIMER_STORAGE_KEY);
      if (!savedEndTime) {
        return;
      }

      const endTime = parseInt(savedEndTime, 10);
      const now = Date.now();
      const newTime = Math.max(0, Math.floor((endTime - now) / 1000));

      setTimeRemaining(newTime);

      if (newTime === 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;

        // Set both completion and dismissal flags when timer reaches zero
        localStorage.setItem('playmerce_game_complete', 'true');
        localStorage.setItem('playmerce_dismissed', 'true');
        
        // Call the completion callback to set app state
        if (onTimerComplete) {
          onTimerComplete();
        }
        
        // Collapse the iframe
        parentFrameMessaging.sendCollapseIframe();
      }
    }, 1000);
  }, [durationSeconds]);

  const resetTimer = useCallback(() => {
    // Don't reset timer if we don't have a valid duration
    if (!durationSeconds || durationSeconds <= 0) {
      return;
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const endTime = Date.now() + durationSeconds * 1000;
    if (isPersistenceEnabled()) {
      localStorage.setItem(TIMER_STORAGE_KEY, endTime.toString());
    }
    setTimeRemaining(durationSeconds);
  }, [durationSeconds]);

  // Start timer when component mounts or when timer is reset, but only if we have a valid duration
  useEffect(() => {
    if (durationSeconds > 0 && timeRemaining > 0 && !timerIntervalRef.current) {
      startTimer();
    }
  }, [timeRemaining, startTimer, durationSeconds]);

  // Function to initialize timer when Game page is opened
  const initializeTimer = useCallback(() => {
    // Don't initialize timer if we don't have a valid duration
    if (!durationSeconds || durationSeconds <= 0) {
      return;
    }

    if (!isPersistenceEnabled()) {
      return;
    }

    const savedEndTime = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!savedEndTime) {
      const endTime = Date.now() + durationSeconds * 1000;
      localStorage.setItem(TIMER_STORAGE_KEY, endTime.toString());
      setTimeRemaining(durationSeconds);
    }
  }, [durationSeconds]);

  // Update timer when durationSeconds changes (when settings load)
  useEffect(() => {
    if (durationSeconds > 0 && timeRemaining === 0) {
      // Settings just loaded, check if we need to initialize from localStorage
      if (isPersistenceEnabled()) {
        const savedEndTime = localStorage.getItem(TIMER_STORAGE_KEY);
        if (savedEndTime) {
          const endTime = parseInt(savedEndTime, 10);
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
          setTimeRemaining(remaining);
        } else {
          setTimeRemaining(durationSeconds);
        }
      } else {
        setTimeRemaining(durationSeconds);
      }
    }
  }, [durationSeconds]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  return { timeRemaining, setTimeRemaining, resetTimer, initializeTimer };
};
